import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { mealAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { getErrorMessage } from '../../utils/errorHandler';

const MealManagement = () => {
  const { toast } = useToast();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameArabic: '',
    contentTurkish: '',
    contentArabic: '',
    order: 1
  });
  const [audioFile, setAudioFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    loadMeals();
  }, []);

  const loadMeals = async () => {
    try {
      const response = await mealAPI.getAll();
      setMeals(response.data);
    } catch (error) {
      console.error('Error loading meals:', error);
      toast({
        title: 'Hata!',
        description: 'Mealler yuklenirken hata olustu.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (meal = null) => {
    if (meal) {
      setEditingMeal(meal);
      setFormData({
        name: meal.name,
        nameArabic: meal.nameArabic,
        contentTurkish: meal.contentTurkish || '',
        contentArabic: meal.contentArabic || '',
        order: meal.order
      });
    } else {
      setEditingMeal(null);
      setFormData({
        name: '',
        nameArabic: '',
        contentTurkish: '',
        contentArabic: '',
        order: meals.length + 1
      });
    }
    setAudioFile(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingMeal(null);
    setAudioFile(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['durationMinutes', 'order'].includes(name) ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('nameArabic', formData.nameArabic);
      formDataToSend.append('author', ''); // Empty author
      formDataToSend.append('description', ''); // Empty description
      formDataToSend.append('contentTurkish', formData.contentTurkish);
      formDataToSend.append('contentArabic', formData.contentArabic);
      formDataToSend.append('durationMinutes', 0); // Will be auto-calculated
      formDataToSend.append('order', formData.order);

      if (audioFile) {
        formDataToSend.append('audioFile', audioFile);
      }
      // No image file needed
      formDataToSend.append('imageFile', new File([], 'dummy.png')); // Dummy file for backend

      // Progress callback
      const onProgress = (progress) => {
        setUploadProgress(progress);
      };

      const audioFileName = audioFile ? audioFile.name : '';

      if (editingMeal) {
        await mealAPI.update(editingMeal.id, formDataToSend, onProgress);
        toast({
          title: '✅ Basarili!',
          description: `Meal basariyla guncellendi! Sure otomatik hesaplandi. ${audioFileName ? `Ses: ${audioFileName}` : ''}`,
          duration: 5000
        });
      } else {
        await mealAPI.create(formDataToSend, onProgress);
        toast({
          title: '✅ Basarili!',
          description: `Meal basariyla eklendi! Sure otomatik hesaplandi. Ses: ${audioFileName}`,
          duration: 5000
        });
      }
      handleCloseDialog();
      loadMeals();
    } catch (error) {
      console.error('Error saving meal:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Check if meal was actually saved (check if it's just a toast/notification error)
      const meals = await mealAPI.getAll();
      const justSaved = meals.data.find(m => m.name === formData.name);
      
      if (justSaved) {
        // Meal was saved successfully despite error
        toast({
          title: '✅ Basarili!',
          description: 'Meal basariyla kaydedildi!',
          duration: 5000
        });
        handleCloseDialog();
        loadMeals();
      } else {
        // Real error
        toast({
          title: '❌ Hata!',
          description: getErrorMessage(error, 'Meal kaydedilirken hata olustu.'),
          variant: 'destructive',
          duration: 7000
        });
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu meali silmek istediginizden emin misiniz?')) return;
    
    try {
      await mealAPI.delete(id);
      toast({
        title: 'Basarili!',
        description: 'Meal basariyla silindi.'
      });
      loadMeals();
    } catch (error) {
      console.error('Error deleting meal:', error);
      toast({
        title: 'Hata!',
        description: 'Meal silinirken hata olustu.',
        variant: 'destructive'
      });
    }
  };

  // Drag-drop functionality removed as per user request
  // Order can now be manually set in the form

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#8B7355]">Yukleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#5C4033]">Meal Yonetimi</h2>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Yeni Meal Ekle
        </Button>
      </div>

      <div className="space-y-3">
        {meals.map((meal, index) => (
          <Card
            key={meal.id}
            className="bg-white border-[#E8DCC8] p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D5C4A1] rounded-full flex items-center justify-center text-[#5C4033] font-bold">
                  {meal.order}
                </div>

                {meal.imageUrl && (
                  <img
                    src={meal.imageUrl}
                    alt={meal.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}

                <div className="flex-1">
                  <h3 className="font-bold text-[#3E3028]">{meal.name}</h3>
                  <p className="text-sm text-[#8B7355]">
                    {meal.nameArabic} • {meal.author} • {meal.duration}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(meal)}
                  className="text-[#8B7355] hover:bg-[#F5EFE7]"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(meal.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {meals.length === 0 && (
        <Card className="bg-white border-[#E8DCC8] p-12">
          <div className="text-center">
            <p className="text-[#8B7355]">
              Henuz meal eklenmemis. Yeni meal eklemek icin yukardaki butona tiklayin.
            </p>
          </div>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F9F6F1]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#5C4033]">
              {editingMeal ? 'Meal Duzenle' : 'Yeni Meal Ekle'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Meal Adi</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Diyanet Meali"
                  className="bg-white border-[#D5C4A1]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Arapca Adi</label>
                <Input
                  name="nameArabic"
                  value={formData.nameArabic}
                  onChange={handleChange}
                  required
                  placeholder="معنى القرآن"
                  className="bg-white border-[#D5C4A1] text-right"
                  style={{ fontFamily: '"Traditional Arabic", serif' }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Turkce Icerik</label>
              <Textarea
                name="contentTurkish"
                value={formData.contentTurkish}
                onChange={handleChange}
                rows={8}
                placeholder="Meal'in Turkce icerigi..."
                className="bg-white border-[#D5C4A1]"
              />
              <p className="text-xs text-[#8B7355]">
                ℹ️ Bu alan meal detay sayfasinda gorunecektir.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Arapca Icerik</label>
              <Textarea
                name="contentArabic"
                value={formData.contentArabic}
                onChange={handleChange}
                rows={8}
                placeholder="محتوى المال العربي..."
                className="bg-white border-[#D5C4A1] text-right"
                style={{ fontFamily: '"Traditional Arabic", serif', fontSize: '18px' }}
              />
              <p className="text-xs text-[#8B7355]">
                ℹ️ Bu alan meal detay sayfasinda gorunecektir.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Sira Numarasi</label>
              <Input
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
                required
                min="1"
                className="bg-white border-[#D5C4A1]"
              />
              <p className="text-xs text-[#8B7355]">
                ℹ️ Sure otomatik olarak ses dosyasindan hesaplanacaktir.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Ses Dosyasi</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setAudioFile(e.target.files[0])}
                  required={!editingMeal}
                  className="bg-white border-[#D5C4A1]"
                />
                <Upload className="h-5 w-5 text-[#8B7355]" />
              </div>
              {editingMeal && !audioFile && (
                <p className="text-xs text-[#8B7355]">Mevcut dosyayi degistirmek icin yeni bir dosya secin.</p>
              )}
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2 pt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8B7355]">Yukleniyor...</span>
                  <span className="text-[#5C4033] font-semibold">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-3" />
                <p className="text-xs text-[#8B7355] text-center">
                  {uploadProgress < 30 && '📤 Ses dosyasi yukleniyor...'}
                  {uploadProgress >= 30 && uploadProgress < 70 && '⏱️ Sure hesaplaniyor...'}
                  {uploadProgress >= 70 && uploadProgress < 100 && '💾 Kaydediliyor...'}
                  {uploadProgress === 100 && '✅ Tamamlandi!'}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCloseDialog}
                disabled={isUploading}
                className="text-[#6B5744]"
              >
                <X className="h-4 w-4 mr-2" />
                Iptal
              </Button>
              <Button
                type="submit"
                disabled={isUploading}
                className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Yukleniyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingMeal ? 'Guncelle' : 'Kaydet'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MealManagement;
